<template>
	<div class="hotel-page-main-content">
		<InternalNavigationBar
			ref="navigationMenu"
			class="hotel-page-main-content__menu"
		>
			<InternalNavigationLink
				v-if="hotelInfo?.Descriptions?.length || hotelInfo?.Attributes?.length"
				v-bind="links.description"
				:ga-attributes="gaSelectContentAttr({ cId: 'menu_item_hotel_description' })"
			/>
			<InternalNavigationLink
				v-if="showCustomerReviews"
				v-bind="links.reviews"
				:ga-attributes="gaSelectContentAttr({ cId: 'menu_item_hotel_reviews' })"
			/>
			<InternalNavigationLink
				v-bind="links.offers"
				:ga-attributes="gaSelectContentAttr({ cId: 'menu_item_hotel_offers' })"
			/>
		</InternalNavigationBar>
		<div class="hotel-page-main-content__infos">
			<div class="hotel-page-main-content__infos-main">
				<HotelInfos
					id="description"
					class="hotel-page-main-content__description"
					:hotel-info="hotelInfo"
					:best-offer-type="bestOfferType"
					:map-data="mapData"
				/>
				<CustomerReviews
					v-if="showCustomerReviews"
					id="reviews"
					:iff-code="hotelInfo.Hotel.IffCode"
					:hotel-name="hotelInfo.Hotel.Name"
					@no-reviews="() => (showCustomerReviews = false)"
				/>
			</div>
			<div :class="OFFER_BOX_TELEPORT.HOTEL_DESCRIPTION"></div>
		</div>
	</div>

	<section
		id="offers"
		ref="offerContainer"
		class="hotel-page-main-content__offers"
	>
		<OfferList v-model:best-offer-type="bestOfferType" />
	</section>
	<div
		ref="linksContainer"
		class="hotel-page-main-content__links"
	>
		<div
			v-if="(hotelInfosVisible || customerReviewsVisible) && !offerListVisible"
			class="rpb_offers__fixed-button-wrapper rpb_container"
		>
			<a
				v-show="showButton"
				class="rpb_offers__fixed-button"
				href="#offers"
				@click.prevent="scrollToAnchorWithDynamicOffset($event)"
			>
				Preise & Termine
			</a>
		</div>
		<section
			v-if="showHotelsNearby"
			id="hotel-page-main-content__section-hotels-nearby"
			v-observe-visibility="{
				callback: visibilityChanged,
				once: true,
				intersection: {
					root: null,
					rootMargin: '100px 0px 0px 0px',
				},
			}"
			class="hotel-page-main-content__section-hotels-nearby"
		>
			<HotelsNearby
				v-if="renderHotelsNearby"
				:cyid="hotelInfo.Location.City.TtCyid"
				:city-name="hotelInfo.Location.City.Name"
				:hotel-name="hotelInfo.Hotel.Name"
				:latitude="hotelInfo.Location.Latitude"
				:longitude="hotelInfo.Location.Longitude"
				:search-distance="hotelInfo.Location.SearchDistance"
				@HotelsNearby:HotelsFound="(hotelsFound) => (showHotelsNearby = hotelsFound)"
			/>
		</section>
		<QuickPillList
			class="hotel-page-main-content__section-quick-pill-list"
			:header-props="headerPropsQuickpill"
			:pills="quickPillList"
			@pill-click="pillClickAnalytics"
		/>
	</div>
</template>

<script lang="ts" setup>
import HotelInfos from '@lmt-rpb/HotelInfos/HotelInfos.vue';
import CustomerReviews from '@lmt-rpb/CustomerReviews/CustomerReviews.vue';
import QuickPillList from '@lmt-rpb/QuickPillList/QuickPillList.vue';
import { scrollToAnchorWithDynamicOffset, throttle } from '@utils/utils';
import { isClientSwitzerland } from '@utils/environmentUtils';
import { onMounted, onUnmounted, ref, computed, useTemplateRef } from 'vue';
import { ObserveVisibility as vObserveVisibility } from 'vue-observe-visibility';
import { OFFER_BOX_TELEPORT } from '@global-js/constants';
import type { OfferType } from '@/interfaces/hotel-list-types/hotel-list-types';
import type { OfferListTypoHotelData } from '@/interfaces/offer-list-types/offer-list-typo-hotel-data';
import { useBoundaryObserver } from '@/js/composables/boundaryObserver';
import OfferList from './OfferList.vue';
import HotelsNearby from '../HotelsNearby/HotelsNearby.vue';
import { useSearchParams } from '@helper/composables/searchParams';
import { gaSelectContentAttr } from 'src/utils/analytics';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import InternalNavigationLink, {
	type InternalNavigationLinkProps,
} from '@lmt-rpb/Molecules/InternalNavigationBar/internals/InternalNavigationLink.vue';
import InternalNavigationBar from '@lmt-rpb/Molecules/InternalNavigationBar/InternalNavigationBar.vue';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

type HotelPageNavLinkAnchors = 'description' | 'reviews' | 'offers';

const props = defineProps<{
	hotelInfo: OfferListTypoHotelData;
	mapData: {
		key: string;
	};
}>();

const hotelInfosVisible = ref(false);
const offerListVisible = ref(false);
const customerReviewsVisible = ref(false);
const renderHotelsNearby = ref(false);
const showHotelsNearby = ref(true);
const showCustomerReviews = ref(true);
const showButton = ref(false);
const menu = useTemplateRef('navigationMenu');
const urlPathname = ref('');
const urlSearchParams = useSearchParams(true);
const bestOfferType = ref<OfferType | null>(null);
const offerContainer = ref<HTMLElement | null>(null);
const mainContentContainer = computed(() => menu.value?.$el);
const hrefWithHash = (hash: HotelPageNavLinkAnchors) => `${urlPathname.value}${urlSearchParams.value}#${hash}`;

const links = computed(() => {
	return {
		description: {
			anchorId: 'description',
			href: hrefWithHash('description'),
			label: 'Hotelbeschreibung',
		},
		reviews: {
			anchorId: 'reviews',
			href: hrefWithHash('reviews'),
			label: 'Bewertungen',
		},
		offers: {
			anchorId: 'offers',
			href: hrefWithHash('offers'),
			label: 'Angebote',
		},
	} satisfies Record<HotelPageNavLinkAnchors, InternalNavigationLinkProps>;
});

useBoundaryObserver(
	{ elementRef: mainContentContainer, shouldBeVisible: true },
	{ elementRef: offerContainer, shouldBeVisible: false, options: { threshold: 0 } }
);

const { isDesktop } = storeToRefs(useBreakpointStore());

const visibilityChanged = (isVisible: boolean) => {
	if (isVisible) renderHotelsNearby.value = true;
};

const handleScroll = throttle(() => {
	const scrollPosition = window.scrollY || document.documentElement.scrollTop;
	showButton.value = isDesktop.value ? scrollPosition >= 800 : scrollPosition >= 720; // Show button if scroll position is greater than or equal to 720/800
}, 150);

const pillClickAnalytics = (index: number) => {
	SelectContentAnalytics.selectContent({
		cId: 'hotelpage_hotels_nearby',
		cType: 'ux_plugin_quickpills',
		index: index + 1,
	});
};

onMounted(() => {
	urlPathname.value = window.location.pathname;
	window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
});

const isCh = isClientSwitzerland();

const headerPropsQuickpill = {
	title: isCh ? 'Beliebte Ferienorte in der Nähe' : 'Beliebte Urlaubsorte in der Nähe',
	mobileTitle: isCh ? 'Ferienorte in der Nähe' : 'Urlaubsorte in der Nähe',
};

const searchParams = useSearchParams();
const quickPillList = computed(() =>
	props.hotelInfo.NearbyDestinations.map((el) => ({
		destination: el.Name,
		url: `${el.URL}?${searchParams.value}`,
		titleTag: el.Name,
	}))
);
</script>

<style lang="scss">
.rpb_offers__fixed-button {
	opacity: 1;
	transition: opacity 0.4s;
	color: $color-white !important;
	background: transparent linear-gradient(200deg, $color-lime-light, $color-extra-light 55%, $color-extra-light) 0 0 no-repeat;
	padding: 1.7rem 3.5rem;
	font-weight: $font-weight-semibold;
	border-radius: 15rem;
	box-shadow: -0.4rem 0.2rem 1.5rem rgb(54 54 54 / 0.24%);
	border: 0.3rem solid #fff;
	font-size: $font-small-1;
	cursor: pointer;
	pointer-events: all;
	text-decoration: none !important;
}

.rpb_offers__fixed-button-wrapper {
	bottom: 3.6rem;
	position: sticky;
	display: flex;
	justify-content: flex-end;
	padding-right: 2rem;
	pointer-events: none;
	z-index: 5;
	padding-bottom: 3rem;

	@media screen and (min-width: $breakpoint-small) {
		justify-content: flex-end;
		padding-right: 3.8rem;
	}

	@media screen and (min-width: $breakpoint-large) {
		padding-bottom: 1rem;
	}

	@media screen and (min-width: $breakpoint-verylarge) {
		padding-right: unset;
	}
}

.hotel-page-main-content {
	@media (max-width: $breakpoint-extralarge - 1) {
		&__offers {
			padding: 0 !important;
		}
	}

	&__links > section {
		padding-top: 3rem;
	}

	@media (min-width: $breakpoint-medium) {
		&__links > section {
			padding-top: 6rem;
		}
	}
}
</style>

<style lang="scss" scoped>
@mixin hotel-page-common-paddings {
	padding-right: 1.6rem;
	padding-left: 1.6rem;

	@include media-query-up($breakpoint-medium) {
		padding-right: $padding-normal;
		padding-left: $padding-normal;
	}

	@include media-query-up($breakpoint-container) {
		padding-right: 0;
		padding-left: 0;
	}
}

.hotel-page-main-content {
	padding: 0 !important;

	&__teleport-offer-box:empty,
	&__teleport-description-offer-box:empty {
		display: none;
	}

	&__links {
		padding-right: 0 !important;
		padding-left: 0 !important;
	}

	&__infos {
		max-width: $breakpoint-container;
		margin: 0 auto;
		display: flex;
		flex-direction: row;
		padding-top: 1.6rem;
		padding-bottom: 1.6rem;
		gap: 2.4rem;

		@include hotel-page-common-paddings;

		&-main {
			display: flex;
			flex-direction: column;
			gap: 2.4rem;
			width: 100%;
		}

		@media (min-width: $breakpoint-extralarge) {
			padding-bottom: 2.4rem;
		}
	}

	&__section-hotels-nearby {
		padding-top: 2rem;
		padding-bottom: 3rem;
		background: $color-light-gray;

		:deep(.quickpill a) {
			background: $color-light-gray;

			&:hover {
				background: $color-white;
			}
		}
	}

	&__menu {
		transition: top 0.15s ease-out;
		margin-top: 1.6rem;
		top: 0;
		position: sticky;
		z-index: 100;

		@include hotel-page-common-paddings;
	}

	:deep(&__section-quick-pill-list) {
		max-width: initial;
		margin-bottom: 0;
		padding-bottom: 5rem;

		div,
		ul {
			max-width: $breakpoint-container;
			margin: auto;
		}
	}

	@media (min-width: $breakpoint-small) {
		&__hotel-map {
			max-width: 31rem;
		}
	}

	@media (min-width: $breakpoint-extralarge) {
		&__infos {
			padding-top: 2.4rem;
			padding-bottom: 1.7rem;
			gap: 3.2rem;

			&-main {
				padding: 0 !important;
			}
		}

		&__description {
			gap: 3.2rem;
			padding: 0;
		}

		&__teleport-description-offer-box {
			max-width: 40rem;
			width: 100%;
			margin-left: auto;

			& > * {
				transition: top 150ms ease-out;
				position: sticky;
				top: 7.5rem;
			}
		}

		&__menu {
			min-height: 6.1rem;
			top: 8.8rem;
		}

		&__section-hotels-nearby {
			padding-top: 7rem;
			padding-bottom: 4.8rem;
		}

		&__section-quick-pill-list {
			padding-top: 7rem;
		}
	}
}

body:not(.page-header--hidden) {
	.hotel-page-main-content__teleport-description-offer-box > * {
		top: 16.5rem;
	}
}

.page-header--hidden .hotel-page-main-content__menu {
	top: 0;
}

:deep(.component-header__title) {
	margin-top: 0.5rem;
}
</style>
